<template>
	<section class="base-table">
    <div class="table"
         cy="table">
      <ul class="table-header flex"
          cy="table-header">
        <li class="flex-center"
            :class="[item.class, { sortable: sortable, 'is-sorted': sortable && sortKey === item.class }]"
            v-for="(item, index) in tableHeader"
            :key="index"
            @click="handleHeaderClick(item)">
          <figure v-if="item.title === 'car'"
                  class="is-car">
            <img src="../assets/carInService.svg" alt="car">
          </figure>

          <span v-else-if="item.title === 'client'"
                class="uppercase">
            Client
          </span>

          <span v-else-if="item.title === 'data intrarii'" class="uppercase">
              Data Intrarii
          </span>

          <span v-else
                class="uppercase">
            {{ lang[item.title] || item.title }}
          </span>

          <img v-if="sortable && sortKey === item.class"
               class="sort-caret"
               :class="sortOrder"
               src="../assets/sort-arrow.svg"
               alt="">
        </li>

        <li v-if="hasRowDetail && !isMobile"
            class="row-expand-cell"
            aria-hidden="true"></li>
      </ul>


      <div v-if="showEmptyState"
           class="table-empty"
           cy="table-empty"
           role="status"
           aria-live="polite">
        <h2 class="table-empty-title" cy="table-empty-title">{{ emptyTitle }}</h2>
        <p v-if="emptySubtitle" class="table-empty-subtitle" cy="table-empty-subtitle">{{ emptySubtitle }}</p>
      </div>

      <template v-for="(comanda, index) in data">
      <ul class="table-row flex"
          :class="{ 'is-expanded': isRowExpanded(index), 'is-mobile-collapsible': isMobile }"
          :cy="`table-row-${index + 1}`"
          @click="handleRowClick(comanda, index)"
          :key="comanda._id || comanda.id || ('row-' + index)">
        <li :class="item.class"
            class="text-center pointer"
            v-for="(item, headerIndex) in tableHeader"
            :key="headerIndex">
          <slot v-if="$scopedSlots['cell-' + item.class]"
                :name="'cell-' + item.class"
                :comanda="comanda"
                :index="index"></slot>

          <span v-else-if="item.class === 'detalii'" :title="comanda.observatii">
            {{ comanda.observatii }}
          </span>

          <span v-else-if="item.class === 'masina'"
                class="capitalize"
                :title="[comanda.marca, comanda.model].filter(Boolean).join(' ')">
            {{ [comanda.marca, comanda.model].filter(Boolean).join(' ') }}
          </span>

          <span v-else-if="item.class === 'client'"
                class="capitalize"
                :title="comanda.client && comanda.client.nume">
            {{ comanda.client && comanda.client.nume }}
          </span>

          <span v-else-if="item.class === 'dataIntrarii'"
                class="capitalize">
            {{ formatDateRo(comanda && comanda.createdAt) }}
          </span>

          <a v-else-if="item.class === 'telefon' && isMobile && comanda.client && comanda.client.telefon"
             :href="`tel:${comanda.client.telefon}`"
             class="phone-link"
             @click.stop>
            {{ comanda.client.telefon }}
          </a>

          <span v-else-if="item.class === 'telefon'">
            {{ comanda.client && comanda.client.telefon }}
          </span>

          <span v-else-if="item.class === 'firma'">
            {{ comanda.client && comanda.client.numeFirma ? comanda.client.numeFirma.slice(0, 14) : '' }}
            {{ comanda.client && comanda.client.numeFirma && comanda.client.numeFirma.length > 14 ? '...' : '' }}
          </span>

          <span v-else-if="item.class === 'status'"
                class="status-cell"
                :title="comanda.status && comanda.status.label">
            <span v-if="comanda.status && comanda.status.color"
                  class="status-dot"
                  :style="{ backgroundColor: comanda.status.color }"
                  aria-hidden="true"></span>
            <span class="status-cell-label">{{ comanda.status && comanda.status.label }}</span>
          </span>

          <span v-else-if="item.class === 'rampa'"
                class="cell-tooltip"
                :data-tooltip="comanda.ramp ? comanda.ramp.label : ''">
            <span class="rampa-value">{{ comanda.ramp && comanda.ramp.label }}</span>
          </span>

          <span v-else>{{ comanda[item.class] }}</span>
        </li>

        <li v-if="isMobile"
            class="expand-toggle">
          <button type="button"
                  cy="expand-comanda-btn"
                  :aria-label="isRowExpanded(index) ? 'Ascunde detalii' : 'Vezi detalii'"
                  :aria-expanded="isRowExpanded(index) ? 'true' : 'false'"
                  @click.stop="toggleExpanded(index)">
            <svg width="14"
                 height="9"
                 viewBox="0 0 14 9"
                 xmlns="http://www.w3.org/2000/svg"
                 aria-hidden="true">
              <path d="M1 1L7 7L13 1"
                    stroke="currentColor"
                    stroke-width="1.8"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>
            </svg>
          </button>
        </li>

        <li v-else-if="hasRowDetail"
            class="row-expand-cell">
          <button type="button"
                  class="row-expand-btn"
                  cy="expand-comanda-btn"
                  :aria-label="isRowExpanded(index) ? 'Ascunde detalii' : 'Vezi detalii'"
                  :aria-expanded="isRowExpanded(index) ? 'true' : 'false'"
                  @click.stop="toggleExpanded(index)">
            <svg width="14"
                 height="9"
                 viewBox="0 0 14 9"
                 xmlns="http://www.w3.org/2000/svg"
                 aria-hidden="true">
              <path d="M1 1L7 7L13 1"
                    stroke="currentColor"
                    stroke-width="1.8"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>
            </svg>
          </button>
        </li>
      </ul>

      <div v-if="hasRowDetail && !isMobile && isRowExpanded(index)"
           class="row-detail"
           :key="'detail-' + (comanda._id || comanda.id || index)">
        <slot name="row-detail" :item="comanda" :index="index"></slot>
      </div>
      </template>
    </div>

    <div v-if="!noPagination && !showEmptyState"
         cy="pagination"
         class="pagination flex-center mtop20px">
      <button @click="backPage"
              cy="back-btn"
              class="mright20px flex-center bg-transparent"
              :disabled="+page === 1">
        <svg width="12px" height="21px" viewBox="0 0 12 21" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <path d="M1.3125,20.2304688 C1.22135371,20.2304688 1.13346396,20.2141929 1.04882812,20.1816406 C0.964192285,20.1490884 0.889323242,20.1002607 0.82421875,20.0351562 C0.694009766,19.9049473 0.62890625,19.7486988 0.62890625,19.5664062 C0.62890625,19.3841137 0.694009766,19.2278652 0.82421875,19.0976562 L9.75,10.171875 L0.9609375,1.40234375 C0.830728516,1.27213477 0.765625,1.11263115 0.765625,0.923828125 C0.765625,0.735025098 0.830728516,0.575521484 0.9609375,0.4453125 C1.09114648,0.315103516 1.2506501,0.25 1.43945312,0.25 C1.62825615,0.25 1.78775977,0.315103516 1.91796875,0.4453125 L11.15625,9.703125 C11.286459,9.83333398 11.3515625,9.98958242 11.3515625,10.171875 C11.3515625,10.3541676 11.286459,10.510416 11.15625,10.640625 L1.78125,20.0351562 C1.71614551,20.1002607 1.64127646,20.1490884 1.55664062,20.1816406 C1.47200479,20.2141929 1.39062539,20.2304688 1.3125,20.2304688 Z" id="" fill="#000000" fill-rule="nonzero" transform="translate(5.990234, 10.240234) rotate(-180.000000) translate(-5.990234, -10.240234) "></path>
          </g>
        </svg>
      </button>
      <span class="font-medium"
            cy="page-number"><template v-if="totalPages > 0">{{ page }} / {{ totalPages }}</template><template v-else>{{ page }}</template></span>
      <button @click="nextPage()"
              cy="next-btn"
              class="mleft20px flex-center bg-transparent"
              :disabled="isNextDisabled">
        <svg width="12px" height="21px" viewBox="0 0 12 21" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <path d="M1.3125,20.2304688 C1.22135371,20.2304688 1.13346396,20.2141929 1.04882812,20.1816406 C0.964192285,20.1490884 0.889323242,20.1002607 0.82421875,20.0351562 C0.694009766,19.9049473 0.62890625,19.7486988 0.62890625,19.5664062 C0.62890625,19.3841137 0.694009766,19.2278652 0.82421875,19.0976562 L9.75,10.171875 L0.9609375,1.40234375 C0.830728516,1.27213477 0.765625,1.11263115 0.765625,0.923828125 C0.765625,0.735025098 0.830728516,0.575521484 0.9609375,0.4453125 C1.09114648,0.315103516 1.2506501,0.25 1.43945312,0.25 C1.62825615,0.25 1.78775977,0.315103516 1.91796875,0.4453125 L11.15625,9.703125 C11.286459,9.83333398 11.3515625,9.98958242 11.3515625,10.171875 C11.3515625,10.3541676 11.286459,10.510416 11.15625,10.640625 L1.78125,20.0351562 C1.71614551,20.1002607 1.64127646,20.1490884 1.55664062,20.1816406 C1.47200479,20.2141929 1.39062539,20.2304688 1.3125,20.2304688 Z" id="" fill="#000000" fill-rule="nonzero"></path>
          </g>
        </svg>
      </button>
    </div>
  </section>
</template>

<script>
	import {mapGetters} from "vuex";

  export default {
		name: 'BaseTable',
    props: {
      tableHeader: {
        type: Array,
        required: true,
        default: () => []
      },
      data: {
        type: Array,
        required: true,
        default: () => []
      },
      noPagination: {
        type: Boolean,
        required: false
      },
      page: {
        type: Number,
        required: false,
        default: () => 1
      },
      totalPages: {
        type: Number,
        required: false,
        default: () => 0
      },
      emptyTitle: {
        type: String,
        required: false,
        default: ''
      },
      emptySubtitle: {
        type: String,
        required: false,
        default: ''
      },
      // Opt-in sortable headers: when true, clicking a header emits `sort`
      // with the column's class, and the active column (sortKey) shows a
      // directional caret driven by sortOrder. Tables that don't pass
      // `sortable` (Comenzi/Devize) keep plain, non-clickable headers.
      sortable: {
        type: Boolean,
        required: false,
        default: false
      },
      sortKey: {
        type: String,
        required: false,
        default: ''
      },
      sortOrder: {
        type: String,
        required: false,
        default: 'asc'
      }
    },
    data () {
      return {
        isMobile: false,
        expandedIndexes: [],
        mql: null
      }
    },
    computed: {
      ...mapGetters(['lang']),
      showEmptyState () {
        return !this.data.length && !!this.emptyTitle
      },
      // Desktop row-expand is opt-in: only tables whose parent provides a
      // #row-detail slot get the chevron + detail panel (so Clienți/Devize
      // which don't pass one are unaffected).
      hasRowDetail () {
        return !!this.$scopedSlots['row-detail']
      },
      isNextDisabled () {
        if (this.totalPages > 0) {
          return +this.page >= this.totalPages
        }
        return this.data.length < 10
      }
    },
    watch: {
      data () {
        this.expandedIndexes = []
      },
      page () {
        this.expandedIndexes = []
      }
    },
    mounted () {
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        this.mql = window.matchMedia('(max-width: 720px)')
        this.isMobile = this.mql.matches
        if (this.mql.addEventListener) {
          this.mql.addEventListener('change', this.handleMediaChange)
        } else if (this.mql.addListener) {
          this.mql.addListener(this.handleMediaChange)
        }
      }
    },
    beforeDestroy () {
      if (this.mql) {
        if (this.mql.removeEventListener) {
          this.mql.removeEventListener('change', this.handleMediaChange)
        } else if (this.mql.removeListener) {
          this.mql.removeListener(this.handleMediaChange)
        }
      }
    },
    methods: {
      handleMediaChange (event) {
        this.isMobile = event.matches
        if (!event.matches) {
          this.expandedIndexes = []
        }
      },
      isRowExpanded (index) {
        return this.expandedIndexes.indexOf(index) !== -1
      },
      handleRowClick (comanda) {
        this.$emit('click-item', comanda)
      },
      handleHeaderClick (item) {
        if (this.sortable) {
          this.$emit('sort', item.class)
        }
      },
      toggleExpanded (index) {
        const position = this.expandedIndexes.indexOf(index)
        if (position === -1) {
          this.expandedIndexes.push(index)
        } else {
          this.expandedIndexes.splice(position, 1)
        }
      },
      goToComanda (item) {
        this.$emit('click-item', item)
      },
      backPage () {
        this.$emit('change-page', +this.page - 1)
      },
      nextPage () {
        this.$emit('change-page', +this.page + 1)
      },
      formatDateRo(ISO) {
        const date = new Date(ISO);

        let formatted = date.toLocaleDateString("ro-RO", {
          day: "numeric",
          month: "short",
          year: "2-digit"
        });

        formatted = formatted.replace(/(\d+)\s(\w+)\.?\s(\d{2})/, (_, d, m, y) => {
          return `${d} ${m.charAt(0).toUpperCase() + m.slice(1)}. '${y}`;
        });

        return formatted;
      }
    },
  }
</script>

<style lang="less">
	.base-table {
    .table {
      min-height: 390px;
    }

    // Status cu bulină colorată (culoarea vine din setările statusului).
    .status-cell {
      display: flex;
      align-items: center;
      gap: 7px;
      min-width: 0;

      .status-dot {
        flex: 0 0 auto;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
      }

      .status-cell-label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    .table-header {
      height: 28px;
      margin-top: 20px;
      li {
        margin-right: 1px;
        border-bottom: 1px solid @dark-gray;
        font-size: var(--fz-table-head, 1.4rem);
        padding: 8px;
      }
    }
    .table-row {
      padding: 6px 0;
      font-size: var(--fz-table, 1.9rem);
      &:hover {
        background-color: rgba(0, 0, 0, .03);
      }
    }

    // Sortable headers (opt-in via the `sortable` prop). The active column's
    // caret flips between ascending (points up) and descending (points down).
    .table-header li.sortable {
      cursor: pointer;
      user-select: none;
    }
    .sort-caret {
      flex: 0 0 auto;
      margin-left: 6px;
      width: 10px;
      height: 7px;
      transition: transform 0.15s ease;

      &.asc {
        transform: rotate(180deg);
      }
    }

    /* Desktop / tablet data-table framing. Mobile (<=720px) keeps its own
       collapsible-card layout (defined per view) and is left untouched. */
    @media only screen and (min-width: 721px) {
      .table {
        margin-top: 20px;
      }

      .table-header {
        height: auto;
        margin-top: 0;
        padding: 0 12px;

        li {
          position: relative;
          margin-right: 0;
          padding: 2px 8px;
          border-bottom: none;
          font-weight: 400;
          letter-spacing: 0.04em;
          color: @text-primary;

          // Linie sub fiecare coloană, inset față de marginile celulei, ca să
          // rămână spațiu între segmente (nu o linie continuă pe tot header-ul).
          // Include și celula goală de expand (ultima).
          &::after {
            content: '';
            position: absolute;
            left: 1px;
            right: 1px;
            bottom: 0;
            border-bottom: 1px solid black;
          }
        }
      }

      .table-row {
        align-items: center;
        padding: 0 12px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        transition: background-color 0.12s ease;

        &:last-of-type {
          border-bottom: none;
        }

        &:hover {
          background-color: rgba(0, 0, 0, 0.035);
        }

        > li {
          // min-width:0 lets the flex cell shrink below content so the text
          // can truncate instead of forcing the column wider / wrapping.
          min-width: 0;
          padding: 6px 8px;

          // One line per cell + ellipsis → uniform row height regardless of how
          // long the data is. Full value is available via the native title
          // tooltip and on row click. .cell-tooltip is excluded so the rampă
          // hover tooltip isn't clipped by its own overflow; .status-cell is
          // excluded so its dot+label flex layout isn't forced to display:block.
          > span:not(.cell-tooltip):not(.status-cell) {
            display: block;
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }

      /* Row-expand affordance + detail panel (opt-in via #row-detail slot). */
      .row-expand-cell {
        flex: 0 0 44px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .row-expand-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: @text-secondary;
        transition: background-color 0.15s ease, color 0.15s ease, transform 0.2s ease;

        &:hover {
          background-color: rgba(0, 0, 0, 0.06);
          color: @text-primary;
        }
      }

      // Open row + its panel read as one connected block: drop the row's divider
      // and tint both the same, and flip the chevron.
      .table-row.is-expanded {
        background-color: #fafafa;
        border-bottom-color: transparent;

        .row-expand-btn {
          color: @text-primary;
          transform: rotate(180deg);
        }
      }

      .row-detail {
        padding: 2px 16px 8px;
        background-color: #fafafa;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        animation: row-detail-in 160ms ease-out both;
      }

      @keyframes row-detail-in {
        from { opacity: 0; transform: translateY(-4px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      @media (prefers-reduced-motion: reduce) {
        .row-detail { animation: none; }
      }
    }
    .table-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 150px;
      padding: 48px 20px;
      text-align: center;
      animation: table-empty-fade 300ms ease-out both;

      .table-empty-title {
        margin: 0 0 6px;
        font-size: 2.2rem;
        font-weight: 500;
        color: @text-primary;
      }

      .table-empty-subtitle {
        max-width: 320px;
        margin: 0;
        font-size: 1.5rem;
        line-height: 1.4;
        color: @text-secondary;
      }
    }

    @keyframes table-empty-fade {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @media (prefers-reduced-motion: reduce) {
      .table-empty { animation: none; }
    }

    .cell-tooltip {
      position: relative;
      display: block;
      width: 100%;

      &[data-tooltip=""] {
        &::after, &::before { display: none; }
      }

      &::after {
        content: attr(data-tooltip);
        position: absolute;
        left: 50%;
        bottom: calc(100% + 10px);
        z-index: 30;
        width: max-content;
        max-width: 220px;
        padding: 6px 10px;
        background-color: #1c1c1e;
        color: #fff;
        font-size: 1.3rem;
        font-weight: 500;
        line-height: 1.3;
        text-align: center;
        white-space: normal;
        border-radius: 6px;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transform: translate(-50%, 4px);
        transition: opacity 0.15s ease, transform 0.15s ease;
      }

      &::before {
        content: '';
        position: absolute;
        left: 50%;
        bottom: calc(100% + 4px);
        z-index: 30;
        border: 5px solid transparent;
        border-top-color: #1c1c1e;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transform: translate(-50%, 4px);
        transition: opacity 0.15s ease, transform 0.15s ease;
      }

      &:hover::after,
      &:hover::before {
        opacity: 1;
        visibility: visible;
        transform: translate(-50%, 0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .cell-tooltip::after,
      .cell-tooltip::before {
        transition: none;
      }
    }
	}
</style>
